import autocompleteClasses from '@mui/material/Autocomplete/autocompleteClasses'
import ListSubheader from '@mui/material/ListSubheader'
import Popper from '@mui/material/Popper'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import Autocomplete from '@mui/material/Autocomplete'
/* eslint-disable @typescript-eslint/no-explicit-any */
import { networkAPI } from 'api/NetworkAPI'
import { organizationAPI } from 'api/OrganizationAPI'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import CaleoInputLabel from 'Components/reusable/CaleoCustomComponents/CaleoInputLabel'
import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { IError } from 'types/error'
import { IAccount, IOrganization } from 'types/userInterfaces'
import { chooseDBTranslation } from 'utils/translations'
import { fuseFiltering } from 'utils/utils'
import { InputField } from 'Components/reusable/InputFields/InputField'
import { List, RowComponentProps, useListRef, ListImperativeAPI } from 'react-window'
import { orderBy } from 'lodash'

const LISTBOX_PADDING = 8

type ItemData = Array<
  | {
      key: number
      group: string
      children: React.ReactNode
    }
  | [React.ReactElement, string, number]
>

function RowComponent({ index, itemData, style }: RowComponentProps & { itemData: ItemData }) {
  const dataSet = itemData[index]
  const inlineStyle = {
    ...style,
    top: ((style.top as number) ?? 0) + LISTBOX_PADDING,
  }

  if ('group' in dataSet) {
    return (
      <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    )
  }

  const { key, ...optionProps } = dataSet[0]

  return (
    <Typography key={key} component="li" {...optionProps} noWrap style={inlineStyle}>
      {dataSet[0].key}
    </Typography>
  )
}

// Adapter for react-window v2
const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement> & {
    internalListRef: React.Ref<ListImperativeAPI>
    onItemsBuilt: (optionIndexMap: Map<string, number>) => void
  }
>(function ListboxComponent(props, ref) {
  const { children, internalListRef, onItemsBuilt, ...other } = props
  const itemData: ItemData = []
  const optionIndexMap = React.useMemo(() => new Map<string, number>(), [])

  ;(children as ItemData).forEach(item => {
    itemData.push(item)
    if ('children' in item && Array.isArray(item.children)) {
      itemData.push(...item.children)
    }
  })

  // Map option values to their indices in the flattened array
  itemData.forEach((item, index) => {
    if (Array.isArray(item) && item[1]) {
      optionIndexMap.set(item[1], index)
    }
  })

  React.useEffect(() => {
    if (onItemsBuilt) {
      onItemsBuilt(optionIndexMap)
    }
  }, [onItemsBuilt, optionIndexMap])

  const theme = useTheme()
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  })
  const itemCount = itemData.length
  const itemSize = smUp ? 36 : 48

  const getChildSize = (child: ItemData[number]) => {
    if (Object.hasOwn(child, 'group')) {
      return 48
    }
    return itemSize
  }

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0)
  }

  // Separate className for List, other props for wrapper div (ARIA, handlers)
  const { className, ...otherProps } = other

  return (
    <div ref={ref} {...otherProps}>
      <List
        className={className}
        listRef={internalListRef}
        key={itemCount}
        rowCount={itemCount}
        rowHeight={index => getChildSize(itemData[index])}
        rowComponent={RowComponent}
        rowProps={{ itemData }}
        style={{
          height: getHeight() + 2 * LISTBOX_PADDING,
          width: '100%',
        }}
        overscanCount={5}
        tagName="ul"
      />
    </div>
  )
})

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
})

/** @notExported */
interface IMemberPickerProps {
  /** The current value. */
  value: IOrganization | IAccount | null
  /** Action to do when the value changes. */
  onChange: (newValue: IOrganization | IAccount) => void
  /** The label text. */
  label?: string
  /** The type of member picker. */
  type?: 'teamOrganization' | 'organization' | 'account'
  /** Whether the field is disabled. */
  disabled?: boolean
  /** The team organizations. */
  teamOrganizations?: IOrganization[]
  /** The selected organizations. */
  selectedOrgs?: IOrganization[]
  /** The selected accounts. */
  selectedAccounts?: IAccount[]
  /** The inset label. */
  insetLabel?: boolean
}

/**
 * Member picker component.
 *
 * @returns Member picker component.
 * @notExported
 */
const MemberPicker: React.FC<IMemberPickerProps> = ({
  value,
  onChange,
  label = 'Organization',
  type = 'organization',
  disabled,
  teamOrganizations,
  selectedOrgs,
  selectedAccounts,
  insetLabel,
}) => {
  const [allowedOrgs, setAllowedOrgs] = useState<IOrganization[]>([])
  const [allowedAccounts, setAllowedAccounts] = useState<IAccount[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [backendError, setBackendError] = useState<IError>()
  const [memberInput, setMemberInput] = useState<string | undefined>(undefined)

  const { i18n } = useTranslation()

  let orgs = useMemo(() => {
    if (type === 'organization') {
      let orgs: IOrganization[] = []

      if (selectedOrgs) {
        orgs = allowedOrgs.filter(org => !selectedOrgs.find(item => item.id === org.id))
      } else {
        orgs = allowedOrgs
      }

      return orgs
    }
    return []
  }, [allowedOrgs, selectedOrgs, type])

  const accounts = useMemo(() => {
    if (type === 'account') {
      let accountsData: IAccount[] = []

      if (selectedAccounts) {
        accountsData = allowedAccounts.filter(account => !selectedAccounts.find(item => item.id === account.id))
      } else {
        accountsData = allowedAccounts
      }

      return accountsData
    }
    return []
  }, [allowedAccounts, selectedAccounts, type])

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      try {
        if (!disabled) {
          setLoading(true)
          if (type === 'organization') {
            const results = await organizationAPI.getAllowedOrganizations(controller)
            setAllowedOrgs(results)
          } else if (type !== 'teamOrganization') {
            setAllowedAccounts(
              (await networkAPI.getPossibleAccounts()).map(account => ({
                ...account,
                fullName: `${account?.Person?.firstName} ${account?.Person?.lastName}`,
              }))
            )
          }
        }
      } catch (error) {
        setBackendError(error as IError)
      } finally {
        setLoading(false)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [disabled, type, teamOrganizations])

  // Use react-window v2's useListRef hook for imperative API access
  const internalListRef = useListRef(null)
  const optionIndexMapRef = React.useRef<Map<string, number>>(new Map())

  const handleItemsBuilt = React.useCallback((optionIndexMap: Map<string, number>) => {
    optionIndexMapRef.current = optionIndexMap
  }, [])
  const pickerIdentifier = `${type}-member-picker`
  const labelId = React.useId()

  if (type === 'teamOrganization' && teamOrganizations) {
    orgs = teamOrganizations
  }

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  const getName = (option: IOrganization | IAccount): string => {
    if (type === 'account') {
      const item = option as IAccount
      if (item.Person) {
        return `${item.Person.firstName} ${item.Person.lastName}`
      } else {
        return item.email
      }
    }
    const item = option as IOrganization
    return chooseDBTranslation(i18n, item).name
  }

  return (
    <div style={{ width: '100%' }}>
      {!insetLabel && <CaleoInputLabel id={labelId} label={label} />}
      <Autocomplete
        data-testid={`autocomplete-${pickerIdentifier}`}
        filterOptions={(options, { inputValue }) =>
          fuseFiltering(options, inputValue, ['translations.name', 'email', 'fullName'])
        }
        loading={loading}
        value={value}
        inputValue={memberInput}
        style={{ marginTop: '0px' }}
        onChange={(_event, newValue) => {
          if (typeof newValue !== 'string') {
            if (type === 'account') {
              onChange(newValue as unknown as IAccount)
            } else {
              onChange(newValue as IOrganization)
            }
          }
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        options={
          (type === 'organization' || type === 'teamOrganization'
            ? orderBy(orgs, [item => getName(item)], ['asc'])
            : orderBy(accounts, [item => getName(item)], ['asc'])) as (IOrganization | IAccount)[]
        }
        getOptionLabel={option => {
          return getName(option)
        }}
        isOptionEqualToValue={(option, value) => {
          return option.id === value.id
        }}
        renderOption={(props, option, state) => {
          const newProps = { ...props, key: getName(option) }
          return [newProps, option, state.index] as React.ReactNode
        }}
        renderInput={params => (
          <InputField
            slotProps={{
              inputLabel: insetLabel ? { shrink: true } : {},
              htmlInput: params?.inputProps,
              input: params?.InputProps,
            }}
            {...params}
            name={pickerIdentifier}
            fullWidth
            size="small"
            margin="dense"
            onChange={event => setMemberInput(event.target.value)}
            variant="outlined"
            insetLabel
            label={insetLabel ? label : undefined}
            ariaLabelledBy={!insetLabel ? labelId : undefined}
          />
        )}
        fullWidth
        disabled={disabled}
        slots={{
          popper: StyledPopper,
        }}
        slotProps={{
          listbox: {
            component: ListboxComponent,
            internalListRef,
            onItemsBuilt: handleItemsBuilt,
          } as any,
        }}
      />
    </div>
  )
}

export default MemberPicker
